import { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import i18n from "@/lib/i18n";

/**
 * Applique la langue préférée du profil à i18n — SAUF si l'URL contient déjà
 * un `?lng=` (ex. lien d'invitation /reset-password?lng=en), qui doit primer.
 */
function applyProfileLang(lang?: string | null) {
  if (!lang) return;
  try {
    const hasUrlLng = new URLSearchParams(window.location.search).has("lng");
    if (!hasUrlLng && i18n.language?.slice(0, 2) !== lang) {
      i18n.changeLanguage(lang);
    }
  } catch {
    // window indisponible (SSR/test) — no-op
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  /** Langue préférée (fr | nl | en) — cf. migration 071. Appliquée à i18n au login. */
  lang?: string | null;
  /** Colonnes choisies par l'utilisateur pour /biens (cf. migration 043 + BiensColumnKey). */
  prefs_columns_biens: string[] | null;
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True si la session courante vient du picker /login/select-user (pas un vrai login Supabase). */
  isDevSession: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  /** Active une "dev session" : charge un profile choisi dans le picker sans passer par Supabase Auth. */
  enterDevSession: (profile: Profile) => void;
  /** Patch local du profile (ex: après UPDATE Supabase d'un sous-ensemble) — évite un re-fetch. */
  patchProfile: (patch: Partial<Profile>) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEV_PROFILE_KEY = "skyline-dev-profile";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,
  isDevSession: false,
  signIn: async () => ({}),
  signOut: async () => {},
  enterDevSession: () => {},
  patchProfile: () => {},
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Fetch the user profile from the `profiles` table. */
async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, first_name, last_name, role, lang, prefs_columns_biens")
    .eq("id", userId)
    .single();

  if (error) {
    console.error("[AuthContext] Failed to fetch profile:", error.message);
    return null;
  }

  return data as Profile;
}

function readDevProfileFromStorage(): Profile | null {
  try {
    const raw = sessionStorage.getItem(DEV_PROFILE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Profile;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialDevProfile = readDevProfileFromStorage();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(initialDevProfile);
  const [isLoading, setIsLoading] = useState(true);
  const [isDevSession, setIsDevSession] = useState<boolean>(!!initialDevProfile);

  // Derived state
  const isAuthenticated = !!session || isDevSession;

  // ----- Bootstrap + listen for auth state changes -----
  // Primary path: onAuthStateChange fires INITIAL_SESSION synchronously on
  // subscribe, which avoids a getSession() call (getSession used to contend
  // with the Supabase web-lock under React StrictMode double-mount).
  //
  // Fallback: under the same StrictMode race, the FIRST subscription can hold
  // the lock, get unsubscribed, and the SECOND subscription's INITIAL_SESSION
  // may never fire — leaving the app stuck on the loading spinner. After 3s
  // without a callback, we explicitly call getSession() as a recovery path.
  // Worst case we fall through to no-session and the user lands on /login.
  useEffect(() => {
    let cancelled = false;
    let resolved = false;

    const applySession = async (newSession: Session | null) => {
      if (cancelled) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        const p = await fetchProfile(newSession.user.id);
        if (cancelled) return;
        setProfile(p);
        applyProfileLang(p?.lang);
      } else if (!isDevSession) {
        // Pas de session ET pas de dev session : on efface le profile.
        // (En dev session, le profile a été chargé depuis sessionStorage.)
        setProfile(null);
      }
      if (!resolved) {
        resolved = true;
        setIsLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      void applySession(newSession);
    });

    const fallbackTimer = setTimeout(() => {
      if (cancelled || resolved) return;
      console.warn(
        "[AuthContext] onAuthStateChange did not fire within 3s, falling back to getSession()",
      );
      supabase.auth
        .getSession()
        .then(({ data: { session: s } }) => {
          if (!cancelled && !resolved) void applySession(s);
        })
        .catch((err) => {
          console.error("[AuthContext] getSession fallback failed:", err);
          if (!cancelled && !resolved) {
            resolved = true;
            setIsLoading(false);
          }
        });
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Sign in with email & password -----
  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error?: string }> => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    },
    [],
  );

  // ----- Sign out -----
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsDevSession(false);
    sessionStorage.removeItem(DEV_PROFILE_KEY);
    // Vide le cache React Query : sinon les données de l'utilisateur précédent
    // (biens, dashboard…) restaient en cache et fuyaient vers le compte suivant
    // connecté dans le même onglet.
    queryClient.clear();
  }, []);

  // ----- Enter dev session (login as a chosen profile, no real Supabase auth) -----
  const enterDevSession = useCallback((p: Profile) => {
    // Nettoie les préférences sessionStorage liées au rôle/utilisateur — sinon
    // un précédent dev login (ex: C-Level avec une multi-sélection de collabs)
    // pollue la nouvelle session (ex: Collaborateur Guillaume hériterait de la
    // sélection C-Level au lieu d'avoir uniquement sa liste par défaut).
    const keys = Object.keys(sessionStorage);
    keys.forEach((k) => {
      if (k === DEV_PROFILE_KEY) return;
      sessionStorage.removeItem(k);
    });
    sessionStorage.setItem(DEV_PROFILE_KEY, JSON.stringify(p));
    setProfile(p);
    setIsDevSession(true);
    // Vide le cache React Query au changement de profil dev (évite que les
    // données du profil précédent s'affichent pour le nouveau).
    queryClient.clear();
  }, []);

  // ----- Patch profile (ex: après UPDATE prefs Supabase) -----
  const patchProfile = useCallback((patch: Partial<Profile>) => {
    setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isLoading,
        isAuthenticated,
        isDevSession,
        signIn,
        signOut,
        enterDevSession,
        patchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}
